import { Suspense, useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import DefaultLayout from './DefaultLayout'
import { authInitializedState, authState } from '../../atoms/auth'
import { getLoginPath } from '../../features/auth/returnTo'
import { useAuthState } from '../../utils/authUtils'

const RouteFallback = () => (
  <div className="mx-auto flex min-h-[45vh] w-full max-w-[1200px] items-center justify-center px-4">
    <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
      화면을 불러오는 중입니다.
    </div>
  </div>
)

const RouteContent = () => (
  <Suspense fallback={<RouteFallback />}>
    <Outlet />
  </Suspense>
)

export const PublicRouteLayout = () => (
  <DefaultLayout>
    <RouteContent />
  </DefaultLayout>
)

export const RequireUser = ({ children }: { children: React.ReactNode }) => {
  const auth = useRecoilValue(authState)
  const initialized = useRecoilValue(authInitializedState)
  const location = useLocation()

  if (!initialized) return <RouteFallback />
  if (!auth.id) {
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={getLoginPath(returnTo)} replace state={{ from: returnTo }} />
  }
  return <>{children}</>
}

export const UserRouteLayout = () => (
  <RequireUser>
    <RouteContent />
  </RequireUser>
)

const RequireAdmin = ({ children }: { children: React.ReactNode }) => {
  const { reloadUser } = useAuthState()
  const location = useLocation()
  const [access, setAccess] = useState<'checking' | 'allowed' | 'guest' | 'forbidden'>('checking')

  useEffect(() => {
    let active = true

    reloadUser().then((user) => {
      if (!active) return
      setAccess(!user ? 'guest' : user.isAdmin === 1 ? 'allowed' : 'forbidden')
    })

    return () => {
      active = false
    }
  }, [reloadUser])

  if (access === 'checking') return <RouteFallback />
  if (access === 'guest') {
    const returnTo = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={getLoginPath(returnTo)} replace state={{ from: returnTo }} />
  }
  if (access === 'forbidden') return <Navigate to="/" replace />
  return <>{children}</>
}

export const AdminRouteLayout = () => (
  <RequireAdmin>
    <DefaultLayout variant="admin" reloadAuth={false}>
      <RouteContent />
    </DefaultLayout>
  </RequireAdmin>
)
